/**
 * NotificationOverlay Component
 * Displays in-app toast notifications at the top of the screen.
 * This is a simple banner/toast - full-screen lottie celebrations are handled by CelebrationModal.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  Animated,
  StyleSheet,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useThemeColors } from "@/providers/ThemeProvider";
import { supabase } from "@/lib/supabase/client";
import { useAuth } from "@/providers/AuthProvider";
import { useRouter, usePathname } from "expo-router";

interface NotificationData {
  id: string;
  title: string;
  body: string;
  lottie_animation?: string;
  action_url?: string;
  notification_type?: string;
}

/**
 * NotificationOverlay - displays in-app toast notifications
 */
export const NotificationOverlay = () => {
  const colors = useThemeColors();
  const { user } = useAuth();
  const router = useRouter();
  const currentPath = usePathname();
  
  const [notification, setNotification] = useState<NotificationData | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const slideAnim = useState(new Animated.Value(-200))[0];
  const fadeAnim = useState(new Animated.Value(0))[0];
  
  // Ref to store the auto-dismiss timeout so we can clear it
  const dismissTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * Ref for dismissNotification to avoid stale closures in setTimeout
   */
  const dismissNotificationRef = useRef<() => void>(() => {});

  /**
   * Show notification with animation
   */
  const showNotification = useCallback((data: NotificationData) => {
    // Clear any existing timeout
    if (dismissTimeoutRef.current) {
      clearTimeout(dismissTimeoutRef.current);
      dismissTimeoutRef.current = null;
    }
    
    setNotification(data);
    setIsVisible(true);

    // Animate toast in
    Animated.parallel([
      Animated.spring(slideAnim, {
        toValue: 0,
        useNativeDriver: true,
        tension: 50,
        friction: 8,
      }),
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 300,
        useNativeDriver: true,
      }),
    ]).start();

    // Auto-dismiss after 5 seconds
    dismissTimeoutRef.current = setTimeout(() => {
      // Use ref to get latest dismissNotification function
      dismissNotificationRef.current();
    }, 5000);
  }, [slideAnim, fadeAnim]);

  /**
   * Dismiss notification with animation
   */
  const dismissNotification = useCallback(() => {
    // Clear the auto-dismiss timeout
    if (dismissTimeoutRef.current) {
      clearTimeout(dismissTimeoutRef.current);
      dismissTimeoutRef.current = null;
    }
    
    // Stop any ongoing animations first to ensure clean state
    slideAnim.stopAnimation();
    fadeAnim.stopAnimation();
    
    Animated.parallel([
      Animated.timing(slideAnim, {
        toValue: -200,
        duration: 300,
        useNativeDriver: true,
      }),
      Animated.timing(fadeAnim, {
        toValue: 0,
        duration: 300,
        useNativeDriver: true,
      }),
    ]).start(() => {
      setIsVisible(false);
      setNotification(null);
    });
  }, [slideAnim, fadeAnim]);

  // Keep the ref in sync with the latest dismissNotification function
  useEffect(() => {
    dismissNotificationRef.current = dismissNotification;
  }, [dismissNotification]);

  /**
   * Mark notification as read
   */
  const markAsRead = async (notificationId: string) => {
    try {
      await supabase
        .from("notifications")
        .update({ is_read: true, read_at: new Date().toISOString() })
        .eq("id", notificationId);
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  /**
   * Handle notification tap
   */
  const handlePress = useCallback(() => {
    if (notification?.id) {
      markAsRead(notification.id);
    }
    
    // Clear the auto-dismiss timeout
    if (dismissTimeoutRef.current) {
      clearTimeout(dismissTimeoutRef.current);
      dismissTimeoutRef.current = null;
    }
    
    // Check if this is a game result notification and we should refresh/navigate
    const notificationType = notification?.notification_type;
    const actionUrl = notification?.action_url;
    
    // Stop any ongoing animations first
    slideAnim.stopAnimation();
    fadeAnim.stopAnimation();
    
    // Reset animation values
    slideAnim.setValue(-200);
    fadeAnim.setValue(0);
    
    // Immediately hide the notification
    setIsVisible(false);
    setNotification(null);
    
    // If it's a game result notification, navigate to the action URL or refresh the current page
    if (notificationType === "game_result" || notificationType === "game_won" || notificationType === "game_lost") {
      // Small delay to allow the notification to close smoothly
      setTimeout(() => {
        if (actionUrl) {
          // Navigate to the action URL (e.g., the game session or home)
          router.push(actionUrl as any);
        } else {
          // If no action URL, refresh the current screen by navigating to same path
          router.replace(currentPath as any);
        }
      }, 100);
    }
  }, [notification, slideAnim, fadeAnim, router, currentPath]);

  /**
   * Subscribe to real-time notifications
   */
  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel("notification-overlay")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "notifications",
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const newNotification = payload.new as NotificationData;
          // Only show notifications that have lottie animations or are important types
          if (newNotification.lottie_animation || 
              (payload.new as any).type === "challenge_approved") {
            showNotification(newNotification);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, showNotification]);

  if (!isVisible || !notification) {
    return null;
  }

  return (
    <View style={styles.container} pointerEvents="box-none">
      {/* Notification toast */}
      <Animated.View
        style={[
          styles.toast,
          {
            backgroundColor: colors.surface,
            borderColor: colors.primary,
            transform: [{ translateY: slideAnim }],
            opacity: fadeAnim,
          },
        ]}
      >
        <TouchableOpacity
          style={styles.toastContent}
          onPress={handlePress}
          activeOpacity={0.9}
        >
          {/* Icon */}
          <View
            style={[
              styles.iconContainer,
              { backgroundColor: colors.primary + "20" },
            ]}
          >
            <Ionicons
              name="notifications"
              size={24}
              color={colors.primary}
            />
          </View>

          {/* Text content */}
          <View style={styles.textContainer}>
            <Text
              style={[styles.title, { color: colors.text }]}
              numberOfLines={1}
            >
              {notification.title}
            </Text>
            <Text
              style={[styles.body, { color: colors.textSecondary }]}
              numberOfLines={2}
            >
              {notification.body}
            </Text>
          </View>

          {/* Dismiss button - wrapped in View to prevent event bubbling */}
          <View>
            <TouchableOpacity
              style={styles.dismissButton}
              onPress={(e) => {
                e.stopPropagation();
                dismissNotification();
              }}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            >
              <Ionicons name="close" size={20} color={colors.textSecondary} />
            </TouchableOpacity>
          </View>
        </TouchableOpacity>
      </Animated.View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 9999,
    elevation: 9999,
  },
  toast: {
    position: "absolute",
    top: 60,
    left: 16,
    right: 16,
    borderRadius: 16,
    borderWidth: 1,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
    elevation: 8,
  },
  toastContent: {
    flexDirection: "row",
    alignItems: "center",
    padding: 16,
  },
  iconContainer: {
    width: 48,
    height: 48,
    borderRadius: 12,
    alignItems: "center",
    justifyContent: "center",
    marginRight: 12,
  },
  textContainer: {
    flex: 1,
  },
  title: {
    fontSize: 16,
    fontWeight: "700",
    marginBottom: 2,
  },
  body: {
    fontSize: 14,
    lineHeight: 18,
  },
  dismissButton: {
    padding: 4,
    marginLeft: 8,
  },
});

export default NotificationOverlay;

